// กองไพ่กรีด — เลื่อนแนวนอนแล้วจัดใบเป็นเส้นโค้งแบบกรีดไพ่ในมือ (Lek สั่ง 2 ส.ค. 69)
// ใช้ร่วมกันสองที่: หน้าจับไพ่ฟรี /card_reading กับพัดไพ่ในห้องแม่หมอ — แก้สูตรที่นี่ที่เดียว
//
// ทำไมต้องมี "ใบกลาง": ไพ่ในพัดซ้อนทับกัน ขอบใบส่วนใหญ่โดนใบข้าง ๆ บัง แตะตรงไหนก็โดนใบบนสุด
// จึงแยกเป็นสองจังหวะ — แตะใบที่ยังไม่อยู่กลาง = เลื่อนมันมากลางก่อน / แตะใบกลาง = เลือกจริง
// (จังหวะเดียวกับ carousel ของแอปช้อปปิ้ง คนคุ้นมืออยู่แล้ว และกันเลือกพลาดใบข้างเคียง)
export default class CardFan {
  static ROTATE = 8 // องศาที่ใบเอียงต่อระยะห่าง 1 ใบจากกลางพัด
  static SHRINK = 0.05 // ใบยิ่งไกลยิ่งเล็กลง (ต่อ 1 ใบ) — ให้รู้สึกว่าใบไกลอยู่ลึกเข้าไป
  static DIM = 0.15 // ใบยิ่งไกลยิ่งจาง (ต่อ 1 ใบ)
  static FAR = 4 // ไกลเกินกี่ใบถึงหยุดหรี่/หยุดย่อ (ไม่งั้นใบท้ายกองหายไปเลย)
  static GLIDE = 80 // ปล่อยนิ้วแล้วไหลต่ออีกกี่ ms ตามความเร็วนิ้ว (ยิ่งต่ำยิ่งหยุดไว)
  static FLING = 2 // แรงเหวี่ยงพาไปต่อได้ไกลสุดกี่ใบ — กันพัด "วิ่งหนี" เลยใบที่เล็ง (Lek ทัก 4 ส.ค. 69)
  static DRAG_MIN = 6 // ขยับเกินกี่ px ถึงนับเป็นลาก (น้อยกว่านั้น = ตั้งใจแตะเลือกใบ)

  // root = กล่องที่มี [data-fan-track] กับปุ่ม [data-fan-step] อยู่ข้างใน
  // onPick(card) = ถูกเรียกตอนผู้ใช้แตะใบกลาง — ผู้เรียกเป็นคนตัดสินว่าจะรับใบนั้นไหม
  constructor(root, { onPick }) {
    this.root = root
    this.onPick = onPick
    this.track = root.querySelector("[data-fan-track]")
    this.cards = [...this.track.querySelectorAll("[data-fan-card]")]
    this.index = Math.floor(this.cards.length / 2)
    this.locked = false
    this.still = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    this.cards.forEach((card, i) => card.addEventListener("click", () => this.tap(i)))
    this.steppers = [...root.querySelectorAll("[data-fan-step]")]
    this.steppers.forEach((button) => {
      button.addEventListener("click", () => this.center(this.index + Number(button.dataset.fanStep)))
    })

    // จัดใบใหม่ตรง ๆ ในตัว handler ไม่ห่อ requestAnimationFrame — rAF ถูกหยุดเมื่อแท็บไม่ได้แสดงผล
    // แล้วพัดจะค้างเป็นรูปเก่าเมื่อกลับมาดู (เจอตอนเทส) งานนี้แค่ 21 ใบ เขียน transform ตรง ๆ ไม่หนัก
    // scroll หยุดนิ่งเกิน 150ms = จบการเลื่อนทุกทาง (ล้อเมาส์/แทร็คแพด) → ดูดเข้าใบเป้าหมาย
    // (แทน scroll-snap ของ CSS ที่ต้องถอดออก เพราะมันแย่งกับการลากด้วยนิ้วที่คุมเองข้างล่าง)
    // ต้องดูดเข้า this.aim (ใบที่ center() กำลังพาไป) ไม่ใช่ใบใกล้สุดตอนนั้น — smooth scroll ช่วงท้าย
    // ช้าลงจน scroll event ห่างเกิน 150ms ได้ ถ้าดูดเข้าใบใกล้สุดจะตัดจังหวะไหลแล้วไปไม่ถึงเป้า (เจอตอนเทส)
    this.aim = null
    this.onScroll = () => {
      this.layout()
      clearTimeout(this.settle)
      if (!this.drag) this.settle = setTimeout(() => this.center(this.aim ?? this.index), 150)
    }
    this.track.addEventListener("scroll", this.onScroll, { passive: true })

    // ลากด้วยนิ้ว/เมาส์คุมเอง 1:1 — ไม่ใช้ momentum ของเบราว์เซอร์ที่เหวี่ยงไพ่วิ่งหนีทีละหลายใบ
    // (CSS ปิด pan แนวนอนของเบราว์เซอร์ด้วย touch-action: pan-y แล้ว เหลือแนวตั้งให้เลื่อนหน้าตามปกติ)
    //
    // นิ้วต้องใช้ touch event ห้ามใช้ pointer event (Lek แจ้งพัดเลื่อนไม่ได้ทั้งสองหน้า 6 ส.ค. 69):
    // พอปิด pan แนวนอนด้วย touch-action แล้ว เบราว์เซอร์ยังชิงนิ้วไปเลื่อนหน้าแนวตั้งได้ตลอดเวลา
    // แล้วยิง pointercancel ตัดการลากทิ้ง — พัดเลยไม่ขยับเลยสักนิด (แนวนอนก็ปิดไปแล้ว)
    // touch event preventDefault ได้ = พอรู้ว่านิ้วไปแนวนอนก็ล็อกแกนไว้ เบราว์เซอร์แย่งไปไม่ได้อีก
    this.drag = null
    this.axis = null // แกนที่ล็อกไว้รอบนี้: "x" = กรีดพัด / "y" = ปล่อยให้เลื่อนหน้า
    this.onTouchStart = (event) => {
      if (event.touches.length !== 1) return

      this.axis = null
      this.startY = event.touches[0].clientY
      this.begin(event.touches[0].clientX, event.timeStamp)
    }
    this.onTouchMove = (event) => {
      if (!this.drag || event.touches.length !== 1) return

      const touch = event.touches[0]
      const dx = touch.clientX - this.drag.x
      const dy = touch.clientY - this.startY
      // ยังไม่พ้นระยะ "แตะ" = ยังไม่รู้ว่าจะกรีดพัดหรือเลื่อนหน้า รอก่อน อย่าเพิ่งยึดนิ้ว
      if (!this.axis) {
        if (Math.abs(dx) < CardFan.DRAG_MIN && Math.abs(dy) < CardFan.DRAG_MIN) return

        this.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y"
        if (this.axis === "y") { this.drag = null; return } // ปัดขึ้นลง = คืนนิ้วให้หน้าเลื่อนตามปกติ
      }
      // ล็อกแกนนอนแล้ว — กันเบราว์เซอร์เปลี่ยนใจไปเลื่อนหน้ากลางคันแล้วตัดการลากทิ้ง
      if (event.cancelable) event.preventDefault()
      this.move(touch.clientX, event.timeStamp)
    }
    this.onTouchEnd = () => this.end()
    this.track.addEventListener("touchstart", this.onTouchStart, { passive: true })
    this.track.addEventListener("touchmove", this.onTouchMove, { passive: false })
    this.track.addEventListener("touchend", this.onTouchEnd)
    this.track.addEventListener("touchcancel", this.onTouchEnd)

    // เมาส์บน desktop — ปล่อยให้ pointer event คุม (นิ้วมี touch event ข้างบนแล้ว ห้ามรับซ้ำ)
    this.onDown = (event) => {
      if (event.pointerType !== "mouse" || event.button !== 0) return

      this.begin(event.clientX, event.timeStamp)
    }
    this.onMove = (event) => {
      if (!this.drag || event.pointerType !== "mouse") return
      if (!this.drag.moved && Math.abs(event.clientX - this.drag.x) < CardFan.DRAG_MIN) return

      this.move(event.clientX, event.timeStamp)
    }
    this.onUp = (event) => {
      if (event.pointerType === "mouse") this.end()
    }
    this.onWheel = () => { this.aim = null } // ล้อเมาส์/แทร็คแพดแทรกกลางไหล = เลิกเป้าเดิม ให้ settle ดูดใบใกล้สุดแทน
    this.track.addEventListener("wheel", this.onWheel, { passive: true })
    this.track.addEventListener("pointerdown", this.onDown)
    window.addEventListener("pointermove", this.onMove)
    window.addEventListener("pointerup", this.onUp)
    window.addEventListener("pointercancel", this.onUp)

    // ตอน connect ความกว้างยังเป็น 0 (พัดในห้องแม่หมอ clone มาจาก <template>) — ResizeObserver
    // ยิงให้เองตอนกล่องมีขนาดจริง ใช้จังหวะนั้นเลื่อนไปตั้งต้นที่ใบกลางกอง
    this.width = 0
    this.observer = new ResizeObserver(() => this.ready())
    this.observer.observe(this.track)
    // ResizeObserver ยิงตอนเบราว์เซอร์วาดหน้าเท่านั้น — แท็บที่ยังไม่ได้แสดงผลจะไม่ยิงเลย
    // แล้วพัดเปิดมาแบนเป็นแถวตรงค้างอยู่ใบแรก (เจอตอนเทส) จึงจัดให้เองรอบหนึ่งตั้งแต่ตอนนี้
    // และเผื่ออีกทีตอนแท็บกลับมาแสดงผล
    this.ready()
    this.onShow = () => { if (!document.hidden) this.ready() }
    document.addEventListener("visibilitychange", this.onShow)
  }

  // กล่องมีความกว้างจริงแล้ว → เลื่อนไปตั้งต้นที่ใบกลางกอง
  // เช็คว่าความกว้าง "เปลี่ยนจริง" ก่อน ไม่งั้นทุกครั้งที่ observer ยิงจะดีดพัดกลับไปใบเดิม
  ready() {
    const width = this.track.clientWidth
    if (!width || width === this.width) return

    this.width = width
    this.center(this.index, false)
    // เบราว์เซอร์รีเซ็ต scroll ของกล่องกลับ 0 หลังโหลดหน้าเสร็จได้ (Turbo restore/scroll anchoring)
    // ตั้งใหม่อีกรอบหลังเฟรมแรก ไม่งั้นพัดเปิดมาค้างอยู่ที่ใบแรกแทนที่จะเป็นใบกลางกอง
    setTimeout(() => this.center(this.index, false), 0)
  }

  // ---- การลาก (นิ้วกับเมาส์ใช้ร่วมกัน ต่างกันแค่ event ที่พามาถึง) ----
  begin(x, t) {
    clearTimeout(this.settle)
    this.aim = null // มือจับพัดแล้ว = ยกเลิกเป้าเดิมที่กำลังไหลไป
    this.drag = { x, scroll: this.track.scrollLeft, lastX: x, lastT: t, speed: 0, moved: false }
  }

  move(x, t) {
    const drag = this.drag
    const dt = t - drag.lastT
    if (dt > 0) drag.speed = (x - drag.lastX) / dt
    drag.lastX = x
    drag.lastT = t
    drag.moved = true
    this.track.scrollLeft = drag.scroll - (x - drag.x)
  }

  end() {
    const drag = this.drag
    this.drag = null
    this.axis = null
    if (!drag?.moved) return

    // click ของใบที่นิ้วเผอิญไปปล่อยทับจะยิงตามมาทันทีหลังปล่อย — กันไว้ไม่ให้กลายเป็นการเลือกไพ่
    this.swiped = true
    setTimeout(() => (this.swiped = false), 0)
    // ไหลต่อตามแรงนิ้วอีกนิดเดียวพอให้ไม่หยุดกึก แต่ไม่เกิน FLING ใบ แล้วดูดเข้าใบนั้นเลย
    const glide = -drag.speed * CardFan.GLIDE / this.step()
    this.center(this.index + Math.round(Math.max(-CardFan.FLING, Math.min(CardFan.FLING, glide))))
  }

  destroy() {
    this.track.removeEventListener("scroll", this.onScroll)
    this.track.removeEventListener("wheel", this.onWheel)
    this.track.removeEventListener("touchstart", this.onTouchStart)
    this.track.removeEventListener("touchmove", this.onTouchMove)
    this.track.removeEventListener("touchend", this.onTouchEnd)
    this.track.removeEventListener("touchcancel", this.onTouchEnd)
    this.track.removeEventListener("pointerdown", this.onDown)
    window.removeEventListener("pointermove", this.onMove)
    window.removeEventListener("pointerup", this.onUp)
    window.removeEventListener("pointercancel", this.onUp)
    document.removeEventListener("visibilitychange", this.onShow)
    clearTimeout(this.settle)
    this.observer.disconnect()
  }

  // ระยะห่างระหว่างจุดกึ่งกลางของใบสองใบที่ติดกัน — ใช้แปลง px เป็นหน่วย "ใบ"
  step() {
    if (this.cards.length < 2) return this.cards[0]?.offsetWidth || 1
    return this.cards[1].offsetLeft - this.cards[0].offsetLeft
  }

  layout() {
    // กล่องยังไม่มีความกว้างจริง (ซ่อนอยู่/เพิ่ง clone มา) — คำนวณตอนนี้ได้ค่าขยะแล้วค้างทั้งพัด
    if (!this.track.clientWidth) return

    const middle = this.track.scrollLeft + this.track.clientWidth / 2
    const step = this.step()
    let nearest = 0
    let smallest = Infinity

    this.cards.forEach((card, i) => {
      const offset = (card.offsetLeft + card.offsetWidth / 2 - middle) / step
      const away = Math.abs(offset)
      const capped = Math.min(away, CardFan.FAR)
      // ตัดมุมไม่ให้เกิน FAR ใบ — จุดหมุนอยู่ใต้ใบไกลมาก ถ้าปล่อยให้หมุน 80° ใบท้ายกองจะเหวี่ยง
      // กลับเข้ามาขวางกลางพัด (ตำแหน่ง scroll ห่างกันไม่พอชดเชยการเหวี่ยง)
      const bounded = Math.max(-CardFan.FAR, Math.min(CardFan.FAR, offset))
      // หมุนรอบจุดที่อยู่ "ใต้ใบ" (transform-origin ใน CSS) = ได้ส่วนโค้งของพัดมาเอง ไม่ต้องเลื่อนขึ้นลงเพิ่ม
      card.style.transform = `rotate(${(bounded * CardFan.ROTATE).toFixed(2)}deg) ` +
                             `scale(${(1 - capped * CardFan.SHRINK).toFixed(3)})`
      card.style.opacity = (1 - capped * CardFan.DIM).toFixed(2)
      card.style.zIndex = String(100 - Math.round(away * 10))
      if (away < smallest) { smallest = away; nearest = i }
    })

    // ใบกลาง = "ใบที่ใกล้กลางที่สุด" เสมอ ห้ามใช้เกณฑ์ระยะ (away < 0.5)
    // เพราะ scroll-snap ไม่ได้หยุดตรงจุดพอดีทุกครั้ง (ไพ่ซ้อนกันด้วย margin ติดลบ) พอหยุดคาบเส้น
    // จะไม่มีใบไหนเป็นใบกลางเลย → แตะเท่าไหร่ก็ไม่ติด ได้แต่เลื่อนไปเลื่อนมา (เจอตอนเทสในห้องแม่หมอ)
    this.cards.forEach((card, i) => card.classList.toggle("is-center", i === nearest))
    this.index = nearest
  }

  center(i, smooth = true) {
    const target = Math.max(0, Math.min(i, this.cards.length - 1))
    const card = this.cards[target]
    if (!card) return

    const left = card.offsetLeft + card.offsetWidth / 2 - this.track.clientWidth / 2
    // ถึงเป้าแล้ว (settle เรียกซ้ำหลังไหลจบ) — เคลียร์เป้าทิ้ง จบรอบ ไม่ scrollTo ซ้ำให้กระตุก
    if (Math.abs(this.track.scrollLeft - left) < 2) {
      this.aim = null
      this.layout()
      return
    }

    this.aim = target
    this.track.scrollTo({ left, behavior: smooth && !this.still ? "smooth" : "auto" })
    // ใบริมสุดเลื่อนต่อไม่ได้แล้ว scroll event จะไม่ยิง — จัดหน้าตาเองรอบหนึ่งกันสถานะค้าง
    this.layout()
  }

  tap(i) {
    if (this.locked || this.swiped) return
    const card = this.cards[i]
    if (card.classList.contains("is-taken")) return
    if (i !== this.index) return this.center(i)

    this.onPick(card)
  }

  // ใบที่ถูกหยิบลงช่องแล้ว — คาไว้ในพัดแบบหรี่ ให้เห็นว่าหยิบไปกี่ใบแล้ว
  take(card) {
    card.classList.add("is-taken")
  }

  release(card) {
    card.classList.remove("is-taken")
  }

  lock(on = true) {
    this.locked = on
    this.root.classList.toggle("is-locked", on)
  }
}
